import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import RnUzurkuvanjeApproveRow from './RnUzurkuvanjeApproveRow';
import RnUzurkuvanjeReleaseRow from './RnUzurkuvanjeReleaseRow';
import DateCurrentInput from '../inputs/DateCurrentInput';
import {
  RnUzurkuvanjeReleasePayload,
  RnUzurkuvanjeThread,
  rnUzurkuvanjeUserId,
  rnUzurkuvanjeUserLabel,
} from '../types/rnUzurkuvanjeRequests';

interface Props {
  thread: RnUzurkuvanjeThread;
  currentUserId?: string;
  viewerCanApproveAsQc: boolean;
  intermediateRequiresQcRelease: boolean;
  viewerCanReleaseAsQc: boolean;
  sampledSubprocessIds: string[];
  pin: string;
  onPinChange: (value: string) => void;
  replyValue: string;
  onReplyChange: (value: string) => void;
  replyDateMode: 'accept' | 'new';
  replyDateValue: string;
  onReplyDateModeChange: (value: 'accept' | 'new') => void;
  onReplyDateValueChange: (value: string) => void;
  onReply: () => void;
  onClose: () => void;
  onApprove: () => void;
  onRelease: (payload: RnUzurkuvanjeReleasePayload) => Promise<boolean>;
}

const formatDateTime = (value?: string | null) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '');
const formatDateOnly = (value?: string | null) => (value && moment(value).isValid() ? moment(value).format('DD.MM.YYYY') : '');

const modeClass = (active: boolean) => `rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
  active
    ? 'border-blue-500 border-l-4 bg-blue-50 text-blue-700'
    : 'border-gray-200 bg-white text-gray-600 hover:bg-blue-50/60'
}`;

const RnUzurkuvanjeThreadCard: React.FC<Props> = ({
  thread,
  currentUserId,
  viewerCanApproveAsQc,
  intermediateRequiresQcRelease,
  viewerCanReleaseAsQc,
  sampledSubprocessIds,
  pin,
  onPinChange,
  replyValue,
  onReplyChange,
  replyDateMode,
  replyDateValue,
  onReplyDateModeChange,
  onReplyDateValueChange,
  onReply,
  onClose,
  onApprove,
  onRelease,
}) => {
  const { t } = useTranslation();
  const isApproved = !!thread.qcApproval?.approvedAt;
  const isClosed = thread.status === 'closed';
  const isCreator = !!currentUserId && rnUzurkuvanjeUserId(thread.createdBy) === currentUserId;
  const isRecipient = !!currentUserId && thread.recipients.some((r) => rnUzurkuvanjeUserId(r.userId) === currentUserId);
  const canSeeApprove = !isClosed && !isApproved && !!currentUserId && !isCreator && (isRecipient || viewerCanApproveAsQc);
  const hasReleaseDecision = !!thread.qcRelease?.decidedAt;
  const canDecideRelease = !hasReleaseDecision && !isClosed && viewerCanReleaseAsQc && !isCreator;
  const threadSubprocessIds = Array.from(new Set((thread.rows || []).map((row) => row.subprocessId)));
  const sampled = threadSubprocessIds.length > 0
    ? threadSubprocessIds.every((id) => sampledSubprocessIds.includes(id))
    : sampledSubprocessIds.length > 0;

  return (
    <div className="rounded-md border border-gray-200">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
        <div className="min-w-0">
          {thread.number && (
            <div className="text-[11px] font-semibold text-blue-700">{thread.number}</div>
          )}
          <div className="text-xs font-semibold text-gray-800">{thread.subject}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium text-gray-500">
            {t('rnUzurkuvanjeRequests.dueDate')}: {formatDateOnly(thread.dateTo) || '—'}
          </span>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
            {t(`rnUzurkuvanjeRequests.priority_${thread.priority}`)}
          </span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
            {t(`rnUzurkuvanjeRequests.status_${thread.status}`)}
          </span>
          {!isClosed && (
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              {t('rnUzurkuvanjeRequests.closeThread')}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2 px-3 py-2">
        {thread.messages.map((msg) => (
          <div key={msg._id} className="rounded bg-gray-50 px-2 py-1.5 text-xs">
            <div className="flex items-center justify-between text-[10px] text-gray-500">
              <span className="font-semibold text-gray-700">{msg.authorName || rnUzurkuvanjeUserLabel(msg.author)}</span>
              <span>{t(`rnUzurkuvanjeRequests.kind_${msg.kind}`)} · {formatDateTime(msg.createdAt)}</span>
            </div>
            <div className="mt-0.5 whitespace-pre-wrap text-gray-800">{msg.text}</div>
          </div>
        ))}
      </div>
      {isApproved && (
        <div className="space-y-2 border-t border-gray-200 px-3 py-2">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
            {t('rnUzurkuvanjeRequests.approvedLine')} · {thread.qcApproval?.approvedByName} · {formatDateTime(thread.qcApproval?.approvedAt)}
          </span>
          {intermediateRequiresQcRelease && (hasReleaseDecision || canDecideRelease) && (
            <RnUzurkuvanjeReleaseRow
              qcRelease={thread.qcRelease}
              canDecide={canDecideRelease}
              onRelease={onRelease}
            />
          )}
        </div>
      )}
      {!isApproved && !isClosed && (
        <div className="space-y-2 border-t border-gray-200 px-3 py-2">
          <div className="flex flex-wrap gap-2">
            {thread.dateTo && (
              <button
                type="button"
                onClick={() => onReplyDateModeChange('accept')}
                className={modeClass(replyDateMode === 'accept')}
              >
                {t('communication.acceptDeadline')}
                <span className="ml-1 font-normal text-gray-500">({formatDateOnly(thread.dateTo)})</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => onReplyDateModeChange('new')}
              className={modeClass(replyDateMode === 'new')}
            >
              {t('communication.setNewDeadline')}
            </button>
          </div>
          {replyDateMode === 'new' && (
            <div className="max-w-[10rem]">
              <DateCurrentInput
                value={replyDateValue}
                onChange={onReplyDateValueChange}
                showIcon
                inputClassName="w-full rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-800 bg-white focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              value={replyValue}
              onChange={(e) => onReplyChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onReply(); } }}
              placeholder={t('rnUzurkuvanjeRequests.replyPlaceholder') || ''}
              className="h-8 flex-1 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
            />
            <button
              type="button"
              onClick={onReply}
              disabled={!replyValue.trim()}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
            >
              {t('rnUzurkuvanjeRequests.reply')}
            </button>
            {canSeeApprove && (
              <RnUzurkuvanjeApproveRow
                pin={pin}
                sampled={sampled}
                onPinChange={onPinChange}
                onApprove={onApprove}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RnUzurkuvanjeThreadCard;
